/**
 * InvoiceDetailController — page /facture-detail.html (EPIC-013 / 13.7).
 *
 * Charge une facture émise depuis `GET /invoices/{id}` (avec lignes,
 * paiements, statut PDP, pdpEligible, remindersEnabled, dateEmission, etc.).
 * Affiche les sections : en-tête (numéro, statut, alertes), lignes,
 * paiements §7.C, timeline relances §7.E, infoblock client et totaux.
 *
 * Actions contextuelles selon `invoice.status` :
 *  - `draft`                → Émettre + Archiver
 *  - `issued`/`sent`        → Émettre un avoir + Archiver + (Renvoyer en B2C)
 *  - autres                 → Télécharger PDF + Archiver
 *
 * Bouton « Renvoyer » :
 *  - B2C → cliquable (POST /invoices/{id}/resend)
 *  - B2B → grisé avec title="En attente PA" (cf. spec 13.B2)
 *
 * Vocabulaire UI strict : « PA » ou « Plateforme Agréée », jamais « SuperPDP » /
 * Wero / Sweego / PDP / NotaFacto dans le DOM rendu.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const INVOICE_STATUS_LABELS = {
  draft: "brouillon",
  issued: "émise",
  sent: "envoyée",
  paid: "payée",
  partially_paid: "partiellement payée",
  overdue: "en retard",
  cancelled: "annulée",
  archived: "archivée",
};

const INVOICE_STATUS_PILL = {
  draft: "nm-pill--indigo",
  issued: "nm-pill--ochre",
  sent: "nm-pill--ochre",
  paid: "nm-pill--olive",
  partially_paid: "nm-pill--blue",
  overdue: "nm-pill--red",
  cancelled: "",
  archived: "",
};

const PAYMENT_METHOD_LABELS = {
  bank_transfer: "Virement bancaire",
  card: "Carte bancaire",
  cash: "Espèces",
  check: "Chèque",
  wero: "Service d'encaissement",
  other: "Autre",
};

const REMINDER_STATUS_LABELS = {
  scheduled: "Programmée",
  sent: "Envoyée",
  failed: "En échec",
  cancelled: "Annulée",
};

// La page publique de la facture est servie par l'app elle-même
// (PublicInvoiceController::showPage(), route GET /facture/{token}), donc même
// origine que cette page : on dérive l'URL de window.location.origin pour
// fonctionner en local comme en prod (cf. décision PO 2026-06-02).
const PUBLIC_LINK_PREFIX = `${window.location.origin}/facture/`;

const HISTORY_EVENT_LABELS = {
  draft_created: "Brouillon créé",
  issued: "Facture émise",
  sent: "Facture envoyée au client",
  paid: "Facture payée",
  partially_paid: "Paiement partiel enregistré",
  overdue: "Facture passée en retard",
  cancelled: "Facture annulée (avoir émis)",
  archived: "Facture archivée",
  payment_recorded: "Paiement enregistré",
  payment_updated: "Paiement modifié",
  payment_archived: "Paiement archivé",
};

export default class InvoiceDetailController extends BaseController {
  static targets = [
    "crumbNumber",
    "overline",
    "title",
    "statusPill",
    "statusLabel",
    "importedBadge",
    "dateLine",
    "lockedBanner",
    "pdpBanner",
    "alertsBanner",
    "alertsBody",
    "downloadPdfBtn",
    "resendBtn",
    "addPaymentBtn",
    "addPaymentLinkInline",
    "issueBtn",
    "cancelBtn",
    "archiveBtn",
    "linesBody",
    "attachmentsSection",
    "attachmentList",
    "paymentsBody",
    "paymentTermsBody",
    "remindersToggle",
    "remindersTimeline",
    "timeline",
    "clientName",
    "clientLines",
    "totalsBody",
    "originBanner",
    "originText",
    "settlementHelpBanner",
    "settlementErrorBanner",
    "settlementErrorBody",
    "publicLinkCard",
    "publicLinkUrl",
    "publicLinkCopyBtn",
  ];

  connect() {
    this._invoiceId = new URLSearchParams(window.location.search).get("id");
    this._invoice = null;
    this._payments = [];
    this._reminderTimeline = [];

    if (!this._invoiceId) {
      this.showError("Identifiant de facture manquant.");
      return;
    }
    this._load();
  }

  async _load() {
    try {
      const [invoice, paymentsResp, remindersResp] = await Promise.all([
        api.getInvoice(this._invoiceId),
        // EPIC-042 / 42.5 — endpoint unifié /v1/payments (filtre invoiceId)
        // en remplacement de l'ancien /v1/invoices/{id}/payments.
        api.payments.list({ invoiceId: this._invoiceId }).catch(() => ({ data: [] })),
        api.getInvoiceReminders(this._invoiceId).catch(() => ({ timeline: [] })),
      ]);
      this._invoice = invoice;
      this._payments = this._normalizePayments(paymentsResp);
      this._reminderTimeline = Array.isArray(remindersResp?.timeline) ? remindersResp.timeline : [];
      this._render();
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger la facture.";
      this.showError(msg);
    }
  }

  /**
   * Normalise une réponse `/v1/payments` (shape `{data: [PaymentTransaction]}`)
   * vers le shape attendu par `_renderPayments()` (champ `method` =
   * `paymentSource`, `date` = `transactionDate`, `reference` = `externalId`).
   * Hérité d'EPIC-042 / 42.5 — évite de réécrire tout le rendu pour un
   * simple renommage de colonnes.
   */
  _normalizePayments(resp) {
    const items = Array.isArray(resp?.data)
      ? resp.data
      : Array.isArray(resp?.payments)
        ? resp.payments
        : [];
    return items.map((p) => ({
      id: p.id,
      date: p.transactionDate ?? p.date ?? null,
      method: p.paymentSource ?? p.method ?? null,
      reference: p.externalId ?? p.reference ?? null,
      amount: p.amount,
      status: p.status,
    }));
  }

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------

  _render() {
    if (!this._invoice) return;
    const inv = this._invoice;

    if (this.hasCrumbNumberTarget) {
      this.crumbNumberTarget.textContent = inv.number ?? "(brouillon)";
    }
    if (this.hasOverlineTarget) {
      this.overlineTarget.textContent = inv.kind === "credit_note"
        ? "Avoir"
        : inv.kind === "advance_payment"
          ? "Facture d'acompte"
          : inv.kind === "settlement"
            ? "Facture de solde"
            : "Facture";
    }
    if (this.hasTitleTarget) this.titleTarget.textContent = inv.number ?? "(brouillon)";

    if (this.hasStatusPillTarget) {
      this._setStatusPill(this.statusPillTarget, this.statusLabelTarget, inv.status);
    }
    if (this.hasImportedBadgeTarget) {
      this.importedBadgeTarget.hidden = inv.origin !== "imported";
    }
    this._renderDateLine(inv);

    this._renderBanners(inv);
    this._renderOriginBanner(inv);
    this._renderActions(inv);
    this._renderLines(inv);
    this._renderAttachments(inv);
    this._renderPayments();
    this._renderPaymentTerms(inv);
    this._renderReminders(inv);
    this._renderHistory(inv);
    this._renderClient(inv);
    this._renderTotals(inv);
    this._renderPublicLink(inv);
  }

  /**
   * Ligne de dates sous le pill de statut : « Émise le jj/mm/aaaa ·
   * Échéance le jj/mm/aaaa ». Brouillon sans date → tiret neutre.
   */
  _renderDateLine(inv) {
    if (!this.hasDateLineTarget) return;
    const parts = [];
    if (inv.dateEmission) parts.push(`Émise le ${this.formatDate(inv.dateEmission)}`);
    if (inv.dateDue) parts.push(`Échéance le ${this.formatDate(inv.dateDue)}`);
    this.dateLineTarget.textContent = parts.length > 0 ? parts.join(" · ") : "—";
  }

  _renderPublicLink(inv) {
    if (!this.hasPublicLinkCardTarget) return;
    const token = inv.publicToken ?? null;
    const status = inv.status;
    const hidden = !token || status === "draft" || status === "archived";
    this.publicLinkCardTarget.hidden = hidden;
    if (hidden) return;
    const url = `${PUBLIC_LINK_PREFIX}${token}`;
    if (this.hasPublicLinkUrlTarget) {
      this.publicLinkUrlTarget.textContent = url;
      this.publicLinkUrlTarget.setAttribute("href", url);
    }
  }

  _renderPaymentTerms(inv) {
    if (!this.hasPaymentTermsBodyTarget) return;
    const raw = inv.status === "draft"
      ? (inv.paymentTermsPreview ?? "")
      : (inv.paymentTerms ?? "");
    const text = typeof raw === "string" ? raw.trim() : "";
    this.paymentTermsBodyTarget.textContent = text === ""
      ? "— Aucune condition particulière —"
      : text;
  }

  _renderOriginBanner(inv) {
    if (!this.hasOriginBannerTarget) return;
    const segments = this._computeOriginSegments(inv);
    if (!segments) {
      this.originBannerTarget.hidden = true;
      return;
    }
    this.originBannerTarget.hidden = false;
    if (!this.hasOriginTextTarget) return;
    while (this.originTextTarget.firstChild) {
      this.originTextTarget.removeChild(this.originTextTarget.firstChild);
    }
    for (const segment of segments) {
      if (segment.href) {
        const a = document.createElement("a");
        a.className = "nm-btn--link";
        a.setAttribute("href", segment.href);
        a.textContent = segment.text;
        this.originTextTarget.appendChild(a);
      } else {
        this.originTextTarget.appendChild(document.createTextNode(segment.text));
      }
    }
  }

  _computeOriginSegments(inv) {
    if (inv.recurringInvoiceId) {
      const segments = [
        { text: "Issue de la facturation récurrente — " },
        {
          text: inv.recurringInvoiceLabel ?? "Récurrence",
          href: `/factures-recurrence-detail.html?id=${encodeURIComponent(inv.recurringInvoiceId)}`,
        },
      ];
      if (inv.quoteId && inv.quoteNumber) {
        segments.push({ text: " · Devis-cadre " });
        segments.push({
          text: inv.quoteNumber,
          href: `/devis-detail.html?id=${encodeURIComponent(inv.quoteId)}`,
        });
      }
      return segments;
    }
    if (inv.quoteId && inv.quoteNumber) {
      const quoteHref = `/devis-detail.html?id=${encodeURIComponent(inv.quoteId)}`;
      if (inv.kind === "milestone" && inv.milestoneIndex && inv.milestoneTotal && inv.milestonePercentage) {
        const pctNum = Number(inv.milestonePercentage);
        const pctStr = Number.isFinite(pctNum)
          ? pctNum.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
          : String(inv.milestonePercentage);
        return [
          { text: `Jalon ${inv.milestoneIndex}/${inv.milestoneTotal} — ${pctStr} % du devis ` },
          { text: inv.quoteNumber, href: quoteHref },
        ];
      }
      if (inv.kind === "advance_payment") {
        return [
          { text: "Acompte sur le devis " },
          { text: inv.quoteNumber, href: quoteHref },
        ];
      }
      return [
        { text: "Issue du devis " },
        { text: inv.quoteNumber, href: quoteHref },
      ];
    }
    if (inv.sourceInvoiceId && inv.parentInvoiceNumber) {
      return [
        { text: "Avoir sur la facture " },
        {
          text: inv.parentInvoiceNumber,
          href: `/facture-detail.html?id=${encodeURIComponent(inv.sourceInvoiceId)}`,
        },
      ];
    }
    return null;
  }

  _renderBanners(inv) {
    if (this.hasSettlementHelpBannerTarget) {
      const showHelp = inv.kind === "settlement" && inv.status === "draft";
      this.settlementHelpBannerTarget.hidden = !showHelp;
    }
    if (this.hasSettlementErrorBannerTarget) {
      this.settlementErrorBannerTarget.hidden = true;
    }
    // Locked banner (statut non-éditable).
    if (this.hasLockedBannerTarget) {
      const locked = inv.status === "archived" || inv.status === "cancelled";
      this.lockedBannerTarget.hidden = !locked;
    }
    // PDP en attente (B2B issued/sent).
    if (this.hasPdpBannerTarget) {
      const isPdpPending = inv.pdpEligible === true
        && (inv.status === "issued" || inv.status === "sent")
        && (inv.pdpStatus === "pending" || inv.pdpStatus === "not_sent" || !inv.pdpStatus);
      this.pdpBannerTarget.hidden = !isPdpPending;
    }
    // Alertes paiement (surpaiement / chargeback / double-paiement).
    if (this.hasAlertsBannerTarget && this.hasAlertsBodyTarget) {
      const alerts = Array.isArray(inv.paymentAlerts) ? inv.paymentAlerts : [];
      if (alerts.length === 0) {
        this.alertsBannerTarget.hidden = true;
      } else {
        this.alertsBodyTarget.textContent = alerts
          .map((a) => a.message ?? a.code ?? "")
          .filter(Boolean)
          .join(" · ");
        this.alertsBannerTarget.hidden = false;
      }
    }
  }

  _renderActions(inv) {
    const isDraft = inv.status === "draft";
    const isIssuedOrSent = inv.status === "issued" || inv.status === "sent";
    const isFinal = inv.status === "archived" || inv.status === "cancelled";
    const clientType = inv.client?.type ?? inv.clientType;
    const isB2B = clientType === "b2b" || clientType === "public_administration";
    const isImported = inv.origin === "imported";

    if (this.hasIssueBtnTarget) this.issueBtnTarget.hidden = !isDraft || isImported;
    if (this.hasCancelBtnTarget) this.cancelBtnTarget.hidden = !isIssuedOrSent;
    if (this.hasArchiveBtnTarget) this.archiveBtnTarget.hidden = isFinal;
    // EPIC-042 / 42.5 — les deux boutons « Ajouter un paiement » sont
    // désormais des liens vers /paiements-edition.html. On y injecte l'id
    // de la facture courante via la query string pour pré-rattachement.
    const editionHref = `/paiements-edition.html?invoiceId=${encodeURIComponent(inv.id)}`;
    const isFullyPaid = inv.status === "paid";
    if (this.hasAddPaymentBtnTarget) {
      this.addPaymentBtnTarget.hidden = isDraft || isFinal || isFullyPaid;
      this.addPaymentBtnTarget.setAttribute("href", editionHref);
    }
    if (this.hasAddPaymentLinkInlineTarget) {
      this.addPaymentLinkInlineTarget.hidden = isDraft || isFinal || isFullyPaid;
      this.addPaymentLinkInlineTarget.setAttribute("href", editionHref);
    }
    if (this.hasResendBtnTarget) {
      this.resendBtnTarget.hidden = !isIssuedOrSent || inv.status === "paid" || isImported;
      if (isB2B) {
        this.resendBtnTarget.disabled = true;
        this.resendBtnTarget.setAttribute(
          "title",
          "En attente PA — la transmission via votre PA sera activée prochainement.",
        );
      } else {
        this.resendBtnTarget.disabled = false;
        this.resendBtnTarget.removeAttribute("title");
      }
    }
    if (this.hasDownloadPdfBtnTarget) {
      this.downloadPdfBtnTarget.setAttribute("href", api.invoicePdfUrl(inv.id));
      this.downloadPdfBtnTarget.setAttribute("target", "_blank");
      this.downloadPdfBtnTarget.setAttribute("rel", "noopener");
    }
  }

  _renderLines(inv) {
    if (!this.hasLinesBodyTarget) return;
    const lines = Array.isArray(inv.lines) ? inv.lines : [];
    while (this.linesBodyTarget.firstChild) {
      this.linesBodyTarget.removeChild(this.linesBodyTarget.firstChild);
    }
    for (const line of lines) {
      const fragment = this.cloneTemplate("tpl-invoice-line-row");
      const unitPrice = line.priceExclVat ?? line.unitPriceExclVat;
      const qtyNum = Number(line.quantity);
      const priceNum = Number(unitPrice);
      const totalHt = Number.isFinite(qtyNum) && Number.isFinite(priceNum)
        ? (qtyNum * priceNum).toFixed(2)
        : null;
      this.fillSlots(fragment, {
        label: line.label ?? "—",
        description: line.description ?? "",
        reference: line.reference ?? "—",
        qty: line.quantity ?? "—",
        unitPrice: this._formatAmount(unitPrice),
        totalHt: this._formatAmount(totalHt),
      });
      const amountExclVat = Number(line.amountExclVat ?? totalHt);
      if (Number.isFinite(amountExclVat) && amountExclVat < 0) {
        const tr = fragment.querySelector("tr");
        if (tr) tr.style.color = "var(--danger, #c0392b)";
      }
      this.linesBodyTarget.appendChild(fragment);
    }
  }

  /**
   * Pièces jointes figées à l'émission. La section reste masquée tant que la
   * facture n'expose pas de pièces jointes (`inv.attachments` absent ou vide) —
   * pas de valeur de démo, pas de spinner perpétuel.
   */
  _renderAttachments(inv) {
    if (!this.hasAttachmentsSectionTarget || !this.hasAttachmentListTarget) return;
    const attachments = Array.isArray(inv.attachments) ? inv.attachments : [];
    while (this.attachmentListTarget.firstChild) {
      this.attachmentListTarget.removeChild(this.attachmentListTarget.firstChild);
    }
    if (attachments.length === 0) {
      this.attachmentsSectionTarget.hidden = true;
      return;
    }
    for (const att of attachments) {
      const fragment = this.cloneTemplate("tpl-invoice-attachment");
      const meta = [att.typeLabel, att.sizeLabel].filter(Boolean).join(" · ");
      this.fillSlots(fragment, {
        name: att.name ?? att.filename ?? "Document",
        meta,
      });
      const link = fragment.querySelector('[data-slot-attr-href="downloadUrl"]');
      if (link) link.setAttribute("href", att.downloadUrl ?? "#");
      this.attachmentListTarget.appendChild(fragment);
    }
    this.attachmentsSectionTarget.hidden = false;
  }

  _renderPayments() {
    if (!this.hasPaymentsBodyTarget) return;
    while (this.paymentsBodyTarget.firstChild) {
      this.paymentsBodyTarget.removeChild(this.paymentsBodyTarget.firstChild);
    }
    if (this._payments.length === 0) {
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = 5;
      td.className = "is-muted";
      td.style.padding = "20px";
      td.style.textAlign = "center";
      td.textContent = "Aucun paiement enregistré.";
      tr.appendChild(td);
      this.paymentsBodyTarget.appendChild(tr);
      return;
    }
    for (const payment of this._payments) {
      const fragment = this.cloneTemplate("tpl-invoice-payment-row");
      this.fillSlots(fragment, {
        date: payment.date ? this.formatShortDate(payment.date) : "—",
        method: PAYMENT_METHOD_LABELS[payment.method] ?? payment.method ?? "—",
        reference: payment.reference ?? "—",
        amount: this._formatAmount(payment.amount),
      });
      const dateEl = fragment.querySelector('[data-slot-attr-datetime="dateIso"]');
      if (dateEl && payment.date) dateEl.setAttribute("datetime", payment.date);
      const btn = fragment.querySelector('[data-slot-attr-data-payment-id="paymentId"]');
      if (btn) {
        btn.dataset.paymentId = payment.id;
        if (payment.status === "archived") btn.disabled = true;
      }
      if (payment.status === "archived") {
        const tr = fragment.querySelector("tr");
        if (tr) tr.classList.add("is-archived");
      }
      this.paymentsBodyTarget.appendChild(fragment);
    }
  }

  _renderReminders(inv) {
    if (this.hasRemindersToggleTarget) {
      this.remindersToggleTarget.checked = !!inv.remindersEnabled;
    }
    if (!this.hasRemindersTimelineTarget) return;
    while (this.remindersTimelineTarget.firstChild) {
      this.remindersTimelineTarget.removeChild(this.remindersTimelineTarget.firstChild);
    }
    if (this._reminderTimeline.length === 0) {
      const li = document.createElement("li");
      li.className = "text-muted fs-13";
      li.textContent = inv.remindersEnabled
        ? "Aucune relance programmée pour cette facture."
        : "Les relances automatiques sont désactivées pour cette facture.";
      this.remindersTimelineTarget.appendChild(li);
      return;
    }
    for (const reminder of this._reminderTimeline) {
      const fragment = this.cloneTemplate("tpl-timeline-item");
      const dateRef = reminder.sentAt ?? reminder.scheduledAt ?? null;
      const label = REMINDER_STATUS_LABELS[reminder.status] ?? reminder.status;
      this.fillSlots(fragment, {
        when: dateRef ? this.formatShortDate(dateRef) : "—",
        what: `${label} — J+${reminder.rank ?? "?"}`,
        who: reminder.variant ?? "",
      });
      this.remindersTimelineTarget.appendChild(fragment);
    }
  }

  _renderHistory(inv) {
    if (!this.hasTimelineTarget) return;
    const events = Array.isArray(inv.events) ? inv.events : [];
    while (this.timelineTarget.firstChild) {
      this.timelineTarget.removeChild(this.timelineTarget.firstChild);
    }
    if (events.length === 0) {
      const li = document.createElement("li");
      li.className = "text-muted fs-13";
      li.textContent = "Aucun évènement enregistré.";
      this.timelineTarget.appendChild(li);
      return;
    }
    for (const event of events) {
      const fragment = this.cloneTemplate("tpl-history-item");
      this.fillSlots(fragment, {
        when: event.occurredAt ? this.formatShortDate(event.occurredAt) : "—",
        what: HISTORY_EVENT_LABELS[event.eventType] ?? event.eventType ?? "Évènement",
        who: event.actorName ? `par ${event.actorName}` : "",
      });
      this.timelineTarget.appendChild(fragment);
    }
  }

  _renderClient(inv) {
    const client = inv.client ?? {};
    if (this.hasClientNameTarget) {
      this.clientNameTarget.textContent = client.legalName ?? "—";
    }
    if (!this.hasClientLinesTarget) return;
    const isB2B = client.type === "b2b" || client.type === "public_administration";
    const addr = client.address;
    const addressLines = addr
      ? [addr.line1, [addr.postalCode, addr.city].filter(Boolean).join(" "), addr.country]
        .filter((s) => s && s.trim() !== "")
      : [];
    const lines = [
      isB2B && client.siret ? `SIRET ${client.siret}` : null,
      isB2B && client.vatIntracom ? `TVA ${client.vatIntracom}` : null,
      ...addressLines,
      client.contactEmail ?? null,
      client.contactPhone ?? null,
    ].filter(Boolean);
    this.clientLinesTarget.textContent = "";
    for (const text of lines) {
      const div = document.createElement("div");
      div.textContent = text;
      this.clientLinesTarget.appendChild(div);
    }
  }

  _renderTotals(inv) {
    if (!this.hasTotalsBodyTarget) return;
    const hasDiscount = Number(inv.discountPercent) > 0;
    const totals = [];
    // En présence d'une remise : Total HT brut + ligne Remise, avant les
    // totaux nets. Sans remise, amountNet* === amount* (colonne générée).
    if (hasDiscount) {
      totals.push({ label: "Total HT avant remise", value: this._formatAmount(inv.amountExclVat) });
      const gross = Number(inv.amountExclVat);
      const net = Number(inv.amountNetExclVat);
      const discount = Number.isFinite(gross) && Number.isFinite(net) ? gross - net : null;
      const pct = this._formatAmount(inv.discountPercent).replace(" €", "");
      const label = inv.discountLabel
        ? `Remise (${pct} %) — ${inv.discountLabel}`
        : `Remise (${pct} %)`;
      totals.push({ label, value: discount === null ? "—" : `−${this._formatAmount(discount)}` });
    }
    totals.push({ label: "Total HT", value: this._formatAmount(inv.amountNetExclVat) });
    totals.push({ label: "TVA", value: this._formatAmount(inv.amountNetVat) });
    totals.push({ label: "Total TTC", value: this._formatAmount(inv.amountNetInclVat), isTotal: true });
    this.totalsBodyTarget.textContent = "";
    for (const row of totals) {
      const div = document.createElement("div");
      div.className = "nm-totals__row" + (row.isTotal ? " is-total" : "");
      const label = document.createElement("span");
      label.textContent = row.label;
      const value = document.createElement("span");
      value.textContent = row.value;
      div.appendChild(label);
      div.appendChild(value);
      this.totalsBodyTarget.appendChild(div);
    }
  }

  _setStatusPill(pillEl, labelEl, status) {
    if (!pillEl) return;
    pillEl.className = "nm-pill";
    const modifier = INVOICE_STATUS_PILL[status];
    if (modifier) pillEl.classList.add(modifier);
    if (labelEl) labelEl.textContent = INVOICE_STATUS_LABELS[status] ?? status;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  downloadPdf() {
    if (!this._invoice) return;
    window.open(api.invoicePdfUrl(this._invoice.id), "_blank", "noopener");
  }

  async resend() {
    if (!this._invoice) return;
    const confirmed = await this.confirm("Renvoyer cette facture au client ?");
    if (!confirmed) return;
    try {
      const inv = await api.resendInvoice(this._invoice.id);
      this._invoice = inv;
      this._render();
      this.showSuccess("Facture renvoyée au client.");
    } catch (error) {
      this._showApiError(error, "Impossible de renvoyer la facture.");
    }
  }

  // openPaymentModal() supprimé en EPIC-042 / 42.5 (D1) — la saisie passe
  // désormais par la page dédiée /paiements-edition.html (les boutons
  // « Ajouter un paiement » sont des <a href> et le href est patché dans
  // _renderActions() avec ?invoiceId=<id>).

  async archivePayment(event) {
    if (!this._invoice) return;
    const paymentId = event.currentTarget.dataset.paymentId;
    if (!paymentId) return;
    const confirmed = await this.confirm("Archiver ce paiement ? Cette action est réversible côté backend.");
    if (!confirmed) return;
    try {
      // EPIC-042 / 42.5 — endpoint POST /v1/payments/{id}/archive
      // (au lieu de DELETE /v1/invoices/{id}/payments/{id}). Le statut
      // de la facture est recalculé côté backend ; on recharge l'invoice
      // complète pour ne pas dupliquer la logique.
      await api.payments.archive(paymentId);
      await this._load();
      this.showSuccess("Paiement archivé.");
    } catch (error) {
      this._showApiError(error, "Impossible d'archiver le paiement.");
    }
  }

  async toggleReminders(event) {
    if (!this._invoice) return;
    const enabled = event.currentTarget.checked;
    try {
      const result = await api.toggleInvoiceReminders(this._invoice.id, enabled);
      if (result?.invoice) this._invoice = result.invoice;
      await this._reloadReminders();
      this._render();
      this.showSuccess(enabled
        ? "Relances automatiques activées."
        : "Relances automatiques désactivées.");
    } catch (error) {
      event.currentTarget.checked = !enabled;
      this._showApiError(error, "Impossible de modifier les relances.");
    }
  }

  async issue() {
    if (!this._invoice) return;
    const confirmed = await this.confirm(
      "Émettre cette facture ? Une fois émise, elle ne pourra plus être modifiée.",
    );
    if (!confirmed) return;
    try {
      const inv = await api.issueInvoice(this._invoice.id);
      this._invoice = inv;
      this._render();
      this.showSuccess(`Facture émise — ${inv.number ?? ""}.`);
    } catch (error) {
      if (this._invoice?.kind === "settlement"
        && error instanceof APIError
        && error.status === 422
      ) {
        this._showSettlementError(error);
        return;
      }
      this._showApiError(error, "Impossible d'émettre la facture.");
    }
  }

  _showSettlementError(error) {
    if (!this.hasSettlementErrorBannerTarget || !this.hasSettlementErrorBodyTarget) {
      this._showApiError(error, "Impossible d'émettre la facture de solde.");
      return;
    }
    const fallback = "La somme des lignes ne correspond pas au montant du devis. Corrigez un brouillon de jalon, émettez un avoir, ou signez un avenant avant de réessayer.";
    this.settlementErrorBodyTarget.textContent = error.message || fallback;
    this.settlementErrorBannerTarget.hidden = false;
    this.settlementErrorBannerTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  async cancelInvoice() {
    if (!this._invoice) return;
    const confirmed = await this.confirm(
      "Émettre un avoir d'annulation pour cette facture ?",
    );
    if (!confirmed) return;
    try {
      const result = await api.cancelInvoice(this._invoice.id, {});
      this.showSuccess(
        `Avoir émis : ${result.creditNote?.number ?? "(brouillon)"}.`,
      );
      if (result?.creditNote?.id) {
        window.location.assign(
          `/facture-detail.html?id=${encodeURIComponent(result.creditNote.id)}`,
        );
      } else {
        await this._load();
      }
    } catch (error) {
      this._showApiError(error, "Impossible d'émettre l'avoir.");
    }
  }

  async archive() {
    if (!this._invoice) return;
    const confirmed = await this.confirm(
      "Archiver cette facture ? Elle ne sera plus modifiable.",
    );
    if (!confirmed) return;
    try {
      const inv = await api.archiveInvoice(this._invoice.id);
      this._invoice = inv;
      this._render();
      this.showSuccess("Facture archivée.");
    } catch (error) {
      this._showApiError(error, "Impossible d'archiver la facture.");
    }
  }

  // ---------------------------------------------------------------------------
  // Recharges partielles
  // ---------------------------------------------------------------------------

  async _reloadPayments() {
    try {
      // EPIC-042 / 42.5 — endpoint unifié /v1/payments (shape
      // `{data: [...]}` normalisée via `_normalizePayments`).
      const resp = await api.payments.list({ invoiceId: this._invoice.id });
      this._payments = this._normalizePayments(resp);
    } catch {
      this._payments = [];
    }
  }

  async _reloadReminders() {
    try {
      const resp = await api.getInvoiceReminders(this._invoice.id);
      this._reminderTimeline = Array.isArray(resp?.timeline) ? resp.timeline : [];
    } catch {
      this._reminderTimeline = [];
    }
  }

  // _handlePaymentSaved supprimé en EPIC-042 / 42.5 (D1) — l'event
  // `payment-entry-modal:saved` n'existe plus depuis le retrait de la
  // modal. L'utilisateur revient sur /paiements.html après la saisie ;
  // un rechargement de la fiche facture reflète le nouveau paiement.

  async copyPublicLink(event) {
    if (event) event.preventDefault();
    if (!this.hasPublicLinkUrlTarget) return;
    const url = this.publicLinkUrlTarget.textContent.trim();
    if (!url) return;
    let copied = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        copied = false;
      }
    }
    if (!copied) {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      }
      document.body.removeChild(ta);
    }
    if (copied) {
      this.showSuccess("Lien copié dans le presse-papier.");
    } else {
      this.showError("Impossible de copier le lien automatiquement. Sélectionnez et copiez-le manuellement.");
    }
  }

  _showApiError(error, fallback) {
    const msg = error instanceof APIError && error.message ? error.message : fallback;
    this.showError(msg);
  }

  _formatAmount(raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return raw;
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  }
}
